import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, ArrowLeft, Download, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useUpgradeStore } from '../stores/upgradeStore';
import * as XLSX from 'xlsx';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { normalizePassportNumber } from '../utils/passportUtils';
import toast from 'react-hot-toast';

type ProcessingResult = {
  passportNumber: string;
  hotelName: string;
  roomType: string;
  pilgrimName?: string;
  status: 'success' | 'error' | 'processing';
  errors: string[];
};

const ITEMS_PER_PAGE = 50;

export const BulkUpgradeStatusPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { translations } = useLanguage();
  const [results, setResults] = useState<ProcessingResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  
  const { createRequest } = useUpgradeStore();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName');

  // Calculate pagination
  const totalPages = Math.ceil(results.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedResults = results.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    const processFile = async () => {
      const state = location.state as { fileData?: string };
      if (!state?.fileData) {
        navigate('/room-upgrade');
        return;
      }

      try {
        setIsProcessing(true);
        const workbook = XLSX.read(state.fileData, { type: 'binary' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(worksheet);
        
        setTotalRows(rows.length);
        
        // Initialize results with processing status
        setResults(rows.map((row: any) => ({
          passportNumber: row.passport_number?.toString() || '',
          hotelName: row.hotel_name?.toString() || '',
          roomType: row.room_type?.toString() || '',
          status: 'processing' as const,
          errors: []
        })));

        for (let i = 0; i < rows.length; i++) {
          const row = rows[i] as any;
          const result = await validateAndProcessRow(row, i);
          
          setCurrentProgress(i + 1);
          setResults(prev => {
            const newResults = [...prev];
            newResults[i] = result;
            return newResults;
          });

          // Add delay between rows to prevent race conditions
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (error: any) {
        console.error('Error processing file:', error);
        setResults(prev => [...prev, {
          passportNumber: 'File Error',
          hotelName: '',
          roomType: '',
          status: 'error',
          errors: [error.message || 'Error processing file']
        }]);
      } finally {
        setIsProcessing(false);
      }
    };

    processFile();
  }, [location.state, navigate]);

  const validateAndProcessRow = async (
    row: any,
    rowIndex: number
  ): Promise<ProcessingResult> => {
    const result: ProcessingResult = {
      passportNumber: row.passport_number?.toString() || '',
      hotelName: row.hotel_name?.toString() || '',
      roomType: row.room_type?.toString().toLowerCase() || '',
      status: 'processing',
      errors: []
    };

    try {
      // Basic validation
      if (!result.passportNumber) {
        throw new Error('Passport number is required');
      }

      if (!result.hotelName) {
        throw new Error('Hotel name is required');
      }

      // Validate room type
      if (!['double', 'triple'].includes(result.roomType)) {
        throw new Error('Room type must be either "double" or "triple"');
      }

      // Normalize passport number
      const normalizedPassport = normalizePassportNumber(result.passportNumber);

      // Find pilgrim in database
      const [stringResults, numberResults] = await Promise.all([
        getDocs(query(
          collection(firestore, 'Pilgrims'),
          where('Passport_Number', '==', normalizedPassport)
        )),
        getDocs(query(
          collection(firestore, 'Pilgrims'),
          where('Passport_Number', '==', parseInt(normalizedPassport))
        ))
      ]);

      const results = [...stringResults.docs, ...numberResults.docs];
      if (results.length === 0) {
        throw new Error(`No pilgrim found with passport number ${result.passportNumber}`);
      }

      const pilgrimDoc = results[0];
      const pilgrimData = pilgrimDoc.data();
      result.pilgrimName = pilgrimData.Name;

      // Find pilgrim's approved link
      const linksRef = collection(firestore, 'PilgrimGuideLinks');
      const linksQuery = query(
        linksRef,
        where('pilgrimId', '==', pilgrimDoc.id),
        where('status', '==', 'approved')
      );
      const linksSnapshot = await getDocs(linksQuery);

      if (linksSnapshot.empty) {
        throw new Error('No approved link found for this pilgrim');
      }

      const linkData = linksSnapshot.docs[0].data();
      
      // Get guide and organizer data to ensure we have names
      let guideName = linkData.guideName;
      let organizerName = linkData.organizerName;
      
      // If guide name is missing or unknown, fetch it
      if (!guideName || guideName === "Unknown" || guideName === "unknown") {
        try {
          const guideDoc = await getDoc(doc(firestore, 'Guides', linkData.guideId));
          if (guideDoc.exists()) {
            guideName = guideDoc.data().name;
          }
        } catch (error) {
          console.error('Error fetching guide:', error);
        }
      }
      
      // If organizer name is missing or unknown, fetch it
      if (!organizerName || organizerName === "Unknown" || organizerName === "unknown") {
        try {
          const organizerDoc = await getDoc(doc(firestore, 'Organizers', linkData.organizerId));
          if (organizerDoc.exists()) {
            organizerName = organizerDoc.data().name;
          }
        } catch (error) {
          console.error('Error fetching organizer:', error);
        }
      }

      // Get the package details to verify hotel and room types
      const packageRef = collection(firestore, 'Packages');
      const packageQuery = query(
        packageRef,
        where('name', '==', pilgrimData.package_nickName)
      );
      const packageSnapshot = await getDocs(packageQuery);

      if (packageSnapshot.empty) {
        throw new Error(`Package "${pilgrimData.package_nickName}" not found`);
      }

      const packageData = packageSnapshot.docs[0].data();

      // Check hotel validity
      let foundHotel = false;
      let currentRoomType: string | null = null;
      let hotelType: 'mainMakkah' | 'shiftingMakkah' | 'madinah' | null = null;
      let startDate = '';
      let endDate = '';

      if (packageData.mainMakkahHotel === result.hotelName) {
        foundHotel = true;
        currentRoomType = pilgrimData.main_makkah_roomType;
        hotelType = 'mainMakkah';
        startDate = packageData.mainMakkahStartDate;
        endDate = packageData.mainMakkahEndDate;
      } else if (packageData.shiftingMakkahHotel === result.hotelName) {
        foundHotel = true;
        currentRoomType = pilgrimData.shifting_makkah_roomType;
        hotelType = 'shiftingMakkah';
        startDate = packageData.shiftingMakkahStartDate;
        endDate = packageData.shiftingMakkahEndDate;
      } else if (packageData.madinahHotel === result.hotelName) {
        foundHotel = true;
        currentRoomType = pilgrimData.madinah_roomType;
        hotelType = 'madinah';
        startDate = packageData.madinahStartDate;
        endDate = packageData.madinahEndDate;
      }

      if (!foundHotel) {
        throw new Error(`Hotel "${result.hotelName}" not found in pilgrim's package`);
      }

      // Validate current room type is higher than target
      if (!currentRoomType) {
        throw new Error('Current room type not found for this hotel');
      }

      if (currentRoomType === 'double') {
        throw new Error('Pilgrim already has a double room - cannot upgrade further');
      }

      if (currentRoomType === 'triple' && result.roomType === 'triple') {
        throw new Error('Pilgrim already has a triple room - must upgrade to double');
      }

      // Check if there's an existing upgrade request
      const requestsRef = collection(firestore, 'UpgradeRequests');
      const requestsQuery = query(
        requestsRef,
        where('pilgrimId', '==', pilgrimDoc.id),
        where('fromHotel', '==', result.hotelName),
        where('status', 'not-in', ['rejected', 'completed', 'cancelled'])
      );
      const requestsSnapshot = await getDocs(requestsQuery);

      if (!requestsSnapshot.empty) {
        throw new Error('There is already an active upgrade request for this pilgrim and hotel');
      }

      // Create the upgrade request with verified guide and organizer names
      await createRequest({
        pilgrimId: pilgrimDoc.id,
        pilgrimName: pilgrimData.Name,
        passportNumber: pilgrimData.Passport_Number,
        guideId: linkData.guideId,
        guideName: guideName || 'Unknown Guide',
        organizerId: linkData.organizerId,
        organizerName: organizerName || 'Unknown Organizer',
        fromHotel: result.hotelName,
        toHotel: result.hotelName, // Same hotel
        fromRoomType: currentRoomType as 'triple' | 'quadruple',
        toRoomType: result.roomType as 'double' | 'triple',
        startDate,
        endDate
      });

      result.status = 'success';
    } catch (error: any) {
      result.status = 'error';
      result.errors.push(error.message);
    }

    return result;
  };

  const handleExport = () => {
    try {
      const exportData = results.map(result => ({
        'Passport Number': result.passportNumber,
        'Pilgrim Name': result.pilgrimName || '-',
        'Hotel': result.hotelName,
        'Room Type': result.roomType,
        'Status': result.status === 'success' ? 'Success' : 'Failed',
        'Errors': result.errors.join(', ') || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 20 }, // Passport Number
        { wch: 30 }, // Pilgrim Name
        { wch: 30 }, // Hotel
        { wch: 15 }, // Room Type
        { wch: 15 }, // Status
        { wch: 50 }, // Errors
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bulk Upgrade Results');
      XLSX.writeFile(wb, `bulk-upgrade-results-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting results:', error);
    }
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;
  const processingCount = results.filter(r => r.status === 'processing').length;
  const successRate = results.length > 0 ? Math.round((successCount / results.length) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">
            {translations?.bulkUpgradeStatus?.title || 'Bulk Upgrade Results'}
          </h1>
          <button
            onClick={() => navigate('/room-upgrade')}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft size={20} />
            <span>{translations?.bulkUpgradeStatus?.backToUpgrade || 'Back to Upgrade Page'}</span>
          </button>
        </div>

        {results.length > 0 && processingCount === 0 && (
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
          >
            <Download size={20} />
            <span>{translations?.bulkUpgradeStatus?.export || 'Export Results'}</span>
          </button>
        )}
      </div>

      {/* Processing Status */}
      {isProcessing && (
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
            <h2 className="text-lg font-medium">
              {translations?.bulkUpgradeStatus?.processing || 'Processing File...'}
            </h2>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentProgress / totalRows) * 100}%` }}
            />
          </div>
          <div className="mt-2 text-sm text-gray-600">
            {currentProgress} / {totalRows} {translations?.bulkUpgradeStatus?.rowsProcessed || 'rows processed'}
          </div>
        </div>
      )}

      {/* Stats */}
      {results.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-xl shadow-sm"
            >
              <div className="text-sm text-gray-600 mb-1">
                {translations?.bulkUpgradeStatus?.totalProcessed || 'Total Processed'}
              </div>
              <div className="text-2xl font-bold text-primary-600">
                {results.length}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-6 rounded-xl shadow-sm"
            >
              <div className="text-sm text-gray-600 mb-1">
                {translations?.bulkUpgradeStatus?.successful || 'Successful'}
              </div>
              <div className="text-2xl font-bold text-green-600">
                {successCount}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-6 rounded-xl shadow-sm"
            >
              <div className="text-sm text-gray-600 mb-1">
                {translations?.bulkUpgradeStatus?.failed || 'Failed'}
              </div>
              <div className="text-2xl font-bold text-red-600">
                {errorCount}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white p-6 rounded-xl shadow-sm"
            >
              <div className="text-sm text-gray-600 mb-1">
                {translations?.bulkUpgradeStatus?.successRate || 'Success Rate'}
              </div>
              <div className="text-2xl font-bold text-blue-600">
                {successRate}%
              </div>
            </motion.div>
          </div>

          {/* Results Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                      {translations?.bulkUpgradeStatus?.tableHeaders?.passportNumber || 'Passport Number'}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                      {translations?.bulkUpgradeStatus?.tableHeaders?.pilgrimName || 'Pilgrim Name'}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                      {translations?.bulkUpgradeStatus?.tableHeaders?.hotel || 'Hotel'}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                      {translations?.bulkUpgradeStatus?.tableHeaders?.roomType || 'Room Type'}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                      {translations?.bulkUpgradeStatus?.tableHeaders?.status || 'Status'}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                      {translations?.bulkUpgradeStatus?.tableHeaders?.errors || 'Errors'}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedResults.map((result, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 text-sm">
                        {result.passportNumber}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {result.pilgrimName || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {result.hotelName}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {result.roomType}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium ${
                          result.status === 'success'
                            ? 'bg-green-100 text-green-800'
                            : result.status === 'error'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {result.status === 'success' ? (
                            <CheckCircle size={14} />
                          ) : result.status === 'error' ? (
                            <XCircle size={14} />
                          ) : (
                            <Loader2 className="animate-spin" size={14} />
                          )}
                          {result.status === 'success'
                            ? translations?.bulkUpgradeStatus?.statusSuccess || 'Success'
                            : result.status === 'error'
                            ? translations?.bulkUpgradeStatus?.statusError || 'Failed'
                            : translations?.bulkUpgradeStatus?.statusProcessing || 'Processing'
                          }
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-red-600">
                        {result.errors.join(', ') || '-'}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {translations?.pagination?.previous || 'Previous'}
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`relative ml-3 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {translations?.pagination?.next || 'Next'}
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      {translations?.pagination?.showing || 'Showing'}{' '}
                      <span className="font-medium">{startIndex + 1}</span>{' '}
                      {translations?.pagination?.to || 'to'}{' '}
                      <span className="font-medium">{Math.min(startIndex + ITEMS_PER_PAGE, results.length)}</span>{' '}
                      {translations?.pagination?.of || 'of'}{' '}
                      <span className="font-medium">{results.length}</span>{' '}
                      {translations?.pagination?.results || 'results'}
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                          currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">{translations?.pagination?.previous || 'Previous'}</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage + 2 >= totalPages) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border ${
                              pageNum === currentPage
                                ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            } text-sm font-medium`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                          currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">{translations?.pagination?.next || 'Next'}</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
